import { useFormContext } from 'react-hook-form';
import {
  EligibilityCheckMethod,
  SignupFormData,
  SignupStep,
  SignUpUserType,
} from 'features/signUp/types';
import { useSignupData } from 'features/signUp/hooks/useSignupData';

const stepFields: Partial<Record<SignupStep, (keyof SignupFormData)[]>> = {
  AccessCodeSubmission: ['accessCode'],
  UserTypeSelection: ['selectedUserType'],
  DobEligibilityCheck: ['memberDateOfBirth'],
  MemberProfileCreation: [
    'memberFirstName',
    'memberLastName',
    'memberDateOfBirth',
    'memberStateOfResidence',
    'memberGender',
  ],
  CaregiverProfileCreation: [
    'caregiverFirstName',
    'caregiverLastName',
    'caregiverDateOfBirth',
    'caregiverStateOfResidence',
    'caregiverRelationshipToMember',
    'caregiverPhoneNumber',
  ],

  AccountCredentials: ['memberEmail', 'memberConfirmedEmail', 'password', 'confirmedPassword'],
  PolicyAcceptance: ['privacyPolicyAccepted', 'eulaPolicyAccepted'],
};

export const getStepFieldsToWatch = (
  currentStep: SignupStep,
  userType: SignUpUserType,
  eligibilityCheckMethod?: EligibilityCheckMethod,
): (keyof SignupFormData)[] => {
  if (currentStep === 'ConditionalEligibilityCheck') {
    return eligibilityCheckMethod === 'memberId' ? ['memberId'] : ['memberLastName'];
  }
  const baseFields = stepFields[currentStep] || [];
  // Extend for MemberProfileCreation if user is not a caregiver
  if (currentStep === 'MemberProfileCreation' && userType !== 'caregiver') {
    return [...baseFields, 'memberPhoneNumber'];
  }
  return baseFields;
};

export const useSignUpFormValidation = (currentStep: SignupStep, userType: SignUpUserType) => {
  const { formState, watch, getValues } = useFormContext<SignupFormData>();
  const { eligibilityCheckMethod } = useSignupData();
  const { errors } = formState;

  // Watch specific fields based on current step
  const stepFieldsToWatch = getStepFieldsToWatch(currentStep, userType, eligibilityCheckMethod);
  const watchedValues = watch(stepFieldsToWatch);

  // Check if all required fields for current step have values
  const hasRequiredValues = () => {
    const values = getValues();
    return stepFieldsToWatch.every((field) => {
      const value = values[field];

      return value !== undefined && value !== '' && value !== null;
    });
  };

  // Check if current step has any validation errors
  const hasStepErrors = () => stepFieldsToWatch.some((field) => errors[field]);

  let canSubmit = hasRequiredValues() && !hasStepErrors();

  // Special handling for PolicyAcceptance step
  if (currentStep === 'PolicyAcceptance') {
    const privacyPolicyAccepted = getValues('privacyPolicyAccepted');
    const eulaPolicyAccepted = getValues('eulaPolicyAccepted');
    canSubmit = canSubmit && privacyPolicyAccepted && eulaPolicyAccepted;
  }

  return {
    canSubmit,
    hasRequiredValues: hasRequiredValues(),
    hasStepErrors: hasStepErrors(),
    errors,
  };
};
